import { create } from 'zustand'
import type { Toast } from '../lib/types'

let toastId = 0

export type WorkspacePanelType = 'calendar' | 'tasks' | 'notes' | 'contacts' | 'chat'
export type AppTheme = 'dark' | 'light'

// Palette CSS vars — must stay in sync with ThemeCustomizer ALL_PALETTE_VARS
const PALETTE_VARS = [
  '--bg-root', '--bg-sidebar', '--bg-panel', '--bg-surface', '--bg-elevated',
  '--bg-input', '--bg-hover', '--bg-selected', '--bg-selected-strong',
  '--border', '--border-strong',
  '--text-primary', '--text-secondary', '--text-tertiary', '--text-disabled',
]

function readLocalStorage<T>(key: string, fallback: T): T {
  try { return (localStorage.getItem(key) as T) ?? fallback } catch { return fallback }
}

interface UIState {
  // Panels
  sidebarCollapsed: boolean
  readingPaneOpen: boolean

  // Toasts
  toasts: Toast[]

  // Modals
  shortcutsModalOpen: boolean
  settingsModalOpen: boolean

  // Keyboard shortcut mode
  shortcutModeActive: boolean

  // Drag & drop
  draggingThreadId: string | null

  // Density
  density: 'compact' | 'comfortable' | 'spacious'

  // Layout mode
  layoutMode: 'split' | 'list'

  // Workspace sidebar (right panel)
  workspacePanel: WorkspacePanelType | null

  // Theme
  theme: AppTheme

  // Filter creator
  filterCreatorOpen: boolean
  filterCreatorDefaults: { from?: string; to?: string; subject?: string }

  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (v: boolean) => void
  toggleReadingPane: () => void
  setLayoutMode: (mode: UIState['layoutMode']) => void
  setWorkspacePanel: (panel: WorkspacePanelType | null) => void
  toggleTheme: () => void

  addToast: (message: string, type?: Toast['type'], opts?: Partial<Toast>) => string
  removeToast: (id: string) => void

  openShortcutsModal: () => void
  closeShortcutsModal: () => void
  openSettingsModal: () => void
  closeSettingsModal: () => void

  openFilterCreator: (defaults?: UIState['filterCreatorDefaults']) => void
  closeFilterCreator: () => void

  setDraggingThread: (id: string | null) => void
  setDensity: (d: UIState['density']) => void
}

// Apply saved theme to <html> immediately (before React mounts) to avoid flash
try {
  const saved = localStorage.getItem('tmail_theme')
  if (saved) document.documentElement.dataset.theme = saved
} catch { /* ignore */ }

export const useUIStore = create<UIState>()((set) => ({
  sidebarCollapsed: false,
  readingPaneOpen: true,
  toasts: [],
  shortcutsModalOpen: false,
  settingsModalOpen: false,
  shortcutModeActive: false,
  draggingThreadId: null,
  density: 'comfortable',
  layoutMode: 'split',
  workspacePanel: null,
  theme: readLocalStorage<AppTheme>('tmail_theme', 'dark'),
  filterCreatorOpen: false,
  filterCreatorDefaults: {},

  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
  toggleReadingPane: () => set((s) => ({ readingPaneOpen: !s.readingPaneOpen })),
  setLayoutMode: (mode) => set({ layoutMode: mode }),
  setWorkspacePanel: (panel) => set({ workspacePanel: panel }),
  toggleTheme: () => set((s) => {
    const next: AppTheme = s.theme === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem('tmail_theme', next) } catch { /* ignore */ }
    document.documentElement.dataset.theme = next
    // Palettes are dark-mode only — clear inline palette vars when switching to light
    if (next === 'light') {
      const root = document.documentElement
      for (const v of PALETTE_VARS) root.style.removeProperty(v)
    }
    return { theme: next }
  }),

  addToast: (message, type = 'info', opts = {}) => {
    const id = String(++toastId)
    const toast: Toast = { id, message, type, duration: 4000, ...opts }
    set((s) => ({ toasts: [...s.toasts, toast] }))
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
    }, toast.duration)
    return id
  },

  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  openShortcutsModal: () => set({ shortcutsModalOpen: true }),
  closeShortcutsModal: () => set({ shortcutsModalOpen: false }),
  openSettingsModal: () => set({ settingsModalOpen: true }),
  closeSettingsModal: () => set({ settingsModalOpen: false }),

  setDraggingThread: (id) => set({ draggingThreadId: id }),
  setDensity: (d) => set({ density: d }),

  openFilterCreator: (defaults = {}) => set({ filterCreatorOpen: true, filterCreatorDefaults: defaults }),
  closeFilterCreator: () => set({ filterCreatorOpen: false, filterCreatorDefaults: {} }),
}))
